//@ts-nocheck

'use client'

import * as S from './LanguageSwitch.styles'

import React, { useMemo, useTransition } from 'react'
import Select from 'react-select'
import { useLocale } from 'next-intl'
import { useRouter } from 'next/navigation'
import { getSiteLocales } from '@caffeina-siad/utils'
import Icon from '../../atoms/Icon/Icon.component'
import { Paragraph } from '../../atoms/Typography/Typography.component'
import { useTranslations } from 'next-intl'

interface LanguageOption {
  value: string
  label: string
  ariaLabel: string
}

interface LanguageSwitchProps {
  options: LanguageOption[]
}

const languages: LanguageOption[] = [
  { value: 'en', label: 'En', ariaLabel: 'English' },
  { value: 'it', label: 'It', ariaLabel: 'Italiano' },
  { value: 'cs', label: 'Cs', ariaLabel: 'Čeština' },
  { value: 'ro', label: 'Ro', ariaLabel: 'Română' },
  { value: 'bg', label: 'Bg', ariaLabel: 'Български' },
  { value: 'de', label: 'De', ariaLabel: 'Deutsch' },
  { value: 'hu', label: 'Hu', ariaLabel: 'Magyar' },
  { value: 'pl', label: 'Pl', ariaLabel: 'Polski' },
  { value: 'fr', label: 'Fr', ariaLabel: 'Français' },
  { value: 'ru', label: 'Ru', ariaLabel: 'Русский' },
  { value: 'sk', label: 'Sk', ariaLabel: 'Slovenčina' },
]

const styles = {
  control: (provided) => ({
    ...provided,
    minHeight: '40px',
    background: 'transparent',
    border: 'none',
    // boxShadow: 'none',
    '&:hover': {
      border: 'none',
      boxShadow: 'none',
    },
  }),
  input: (provided) => ({
    ...provided,
    background: 'transparent',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
}

const LanguageSwitch = ({ pageLocalizations }) => {
  const [isPending, startTransition] = useTransition()
  let router = null

  try {
    router = useRouter()
  } catch (e) {
    // do nothing
  }
  const locale = useLocale()
  const t = useTranslations()

  const setDefaultValue = () => {
    if (locale) {
      return availableLanguages.find((option) => option.value === locale)
    }
    return availableLanguages[0]
  }

  const findLocalization = (localizations, lang) => {
    return localizations?.find((loc) => loc.slug?.startsWith(`${lang}/`))
  }

  const availableLanguages = useMemo(() => {
    const siteLocales = getSiteLocales(process.env.NEXT_PUBLIC_SITE_NAME)
    const siteLanguages = languages.filter((language) =>
      siteLocales.includes(language.value)
    )

    if (!pageLocalizations?.length) {
      return siteLanguages.filter((language) => language.value === locale)
    }

    return siteLanguages.filter(
      (lang) => lang.value === locale || findLocalization(pageLocalizations, lang.value)
    )
  }, [pageLocalizations, locale])

  const handleRedirect = (pageLocalizations, selectedLang) => {
    if (!router) return

    const targetLocalization = findLocalization(pageLocalizations, selectedLang)

    if (!targetLocalization || targetLocalization.slug.indexOf('homepage') > -1) {
      startTransition(() => {
        window.location.href = `/${selectedLang}`
      })
    } else {
      startTransition(() => {
        window.location.href = `/${targetLocalization.slug}`
      })
    }
  }

  const handleOnChange = (selectedOption: any, locale: string) => {
    if (locale === selectedOption.value) return

    handleRedirect(pageLocalizations, selectedOption.value)
  }

  const CustomOption = (props) => {
    return (
      <S.LanguageOption aria-selected={props.value === locale}>
        <Icon
          name={`flag-${props.value}`}
          ariaLabel={props.ariaLabel}
          aria-hidden="true"
          useIconColor
        />
        <Paragraph content={props.label} />
      </S.LanguageOption>
    )
  }

  return (
    <S.LanguageSwitch>
      <Select
        aria-label={t('languageSwitch.changeLanguage')}
        options={availableLanguages}
        formatOptionLabel={CustomOption}
        styles={styles}
        onChange={(selectedOption: any) =>
          handleOnChange(selectedOption, locale)
        }
        defaultValue={setDefaultValue()}
        isSearchable={false}
      />
    </S.LanguageSwitch>
  )
}

export default LanguageSwitch
